// Cart.js - Updated with shared helper
import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';

// Shared helper function that you can also export and use elsewhere
export const extractNumericPrice = (price) => {
  if (price === null || price === undefined) {
    return 0;
  }
  
  if (typeof price === 'number') {
    return price;
  }
  
  if (typeof price === 'string') {
    const numericValue = parseFloat(price.replace(/[^0-9.-]+/g, ''));
    return isNaN(numericValue) ? 0 : numericValue;
  }
  
  // Try to convert other types
  try {
    const numericValue = parseFloat(price);
    return isNaN(numericValue) ? 0 : numericValue;
  } catch (error) {
    return 0;
  }
};

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
  };

  // Use the shared helper function
  const formatPrice = (price) => {
    return extractNumericPrice(price);
  };

  // Helper function to display price in currency format
  const displayPrice = (price) => {
    const numericPrice = formatPrice(price);
    return `P ${numericPrice.toLocaleString()}`;
  };

  if (cart.items.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="cart-header">
            <h1>Shopping Cart</h1>
          </div>
          <div className="empty-cart">
            <h2>Your cart is empty</h2>
            <p>Browse our products and add items to your cart.</p>
            <Link to="/" className="btn btn-primary">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <button onClick={clearCart} className="clear-cart-btn">
            Clear Cart
          </button>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cart.items.map(item => {
              // Calculate item total safely
              const itemPrice = formatPrice(item.price);
              const itemTotal = itemPrice * item.quantity;
              
              return (
                <div key={item.id} className="cart-item">
                  <div className="item-image">
                    <div className="image-placeholder">
                      {item.brand && (typeof item.brand === 'object' ? (item.brand.display_name || item.brand.name || 'Unknown Brand') : item.brand)}
                    </div>
                  </div>
                  
                  <div className="item-details">
                    <h3 className="item-name">{item.name}</h3>
                    <p className="item-brand">{item.brand && (typeof item.brand === 'object' ? (item.brand.display_name || item.brand.name || 'Unknown Brand') : item.brand)}</p>
                    <p className="item-price">{displayPrice(item.price)}</p>
                  </div>

                  <div className="item-quantity">
                    <label>Qty:</label>
                    <div className="quantity-controls">
                      <button 
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button 
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="item-total">
                    P {itemTotal.toLocaleString()}
                  </div>

                  <button 
                    className="remove-item-btn"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cart-summary">
            <h3>Order Summary</h3>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>P {getCartTotal().toLocaleString()}</span>
            </div>
            <div className="summary-row">
              <span>Shipping:</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="summary-row">
              <span>Tax:</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>P {getCartTotal().toLocaleString()}</span>
            </div>
            
            <div className="cart-actions">
              <Link to="/" className="continue-shopping-btn">
                Continue Shopping
              </Link>
              <Link to="/checkout" className="checkout-btn btn-primary">
                Proceed to Checkout
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;